import { ChangeDetectorRef, Injectable } from '@angular/core';
import { PagedResult } from '@shared/services/paged-result';
import { customDateFormat, DateService } from '@shared/services/date.service';
import { LanguageService } from '@shared/services/language.service';
import { TranslateService } from '@ngx-translate/core';
import { InstChartData } from '@shared/models/inst-chart-data';
import { AvgChartData } from '@shared/models/avg-chart-data';
import {
 COLORS
} from '@equipment/components/equipment-details/equipment-charts/fluid-charts/fluid-charts.component';
export enum CHART_DATA_TYPE {
  INST,
  AVG
}

export enum CHART_SERIES_TYPE {
  Y = 'Y',
  ALARMS = 'ALARMS',
  REFILLS = 'REFILLS',
  MOVING_AVERAGE = 'MOVING_AVERAGE'
}
import { SeriesGrouperService } from '@equipment/services/series-grouper.service';
import { AxisSetExtremesEventObject } from 'highcharts/highstock';
import { UserPreferencesService } from '@shared/services/user-preferences.service';
import {
  FilterCategory
} from '@equipment/components/equipment-details/equipment-charts/dynamic-filter/dynamic-filter.component';
import { UnitConversionService } from '@shared/services/unit-convertion.service';
export const BASE_CHART_OPTIONS: Highcharts.Options = {
  chart: {
    zooming: {
      type: 'x'
    },
    marginTop: 100,
    height: 600
  },
  rangeSelector: {
    buttons: [
      {
        type: 'all',
        text: 'Reset',
      },
    ],
    inputEnabled: false,
    enabled: true,
    verticalAlign: 'bottom',
  },
  navigator: {
    enabled: true,
    xAxis: {
      labels: {
        style: {
          color: '#000000',
        },
      },
    },
  },
  scrollbar: { enabled: true },
  exporting: {
    buttons: {
      contextButton: {
        menuItems: ['viewFullscreen'],
        symbol: 'url(../../../../../../assets/images/open_in_full.png)',
        symbolSize: 20,
      },
    },
  },
  colors: [], // Placeholder for dynamic color array (set in the component)
  title: {
    text: '',
  },
  tooltip: {
    shared: true,
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 15,
  },
  legend: {
    layout: 'horizontal',
    align: 'center',
    verticalAlign: 'top',
    floating: true,
  },
  responsive: {
    rules: [
      {
        condition: { maxWidth: 700 },
        chartOptions: {
          legend: { enabled: false },
          yAxis: [
            {
              labels: { align: 'right', x: 0, y: -6 },
              showLastLabel: true,
            },
            {
              labels: { align: 'left', x: 0, y: -6 },
              showLastLabel: true,
            },
            {
              visible: false,
            },
          ],
        },
      },
    ],
  },
  credits: { enabled: false },
};
export const COMMON_EVENT_SERIES =       {
  key: 'events',
  translationSlug: 'website.equipment.detail.charts.events',
  checked: true,
  filters: [
    {
      key: 'fluid_alarm',
      value: 'level',
      useValueAsKey: true,
      type: CHART_SERIES_TYPE.ALARMS,
      translationSlug: 'website.equipment.detail.charts.level_alarm',
      valueSuffix: '',
      checked: true,
      showInChart: true,
      tooltipSeries: null,
      dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
      color: '#FF0000',
      shape: 'flag',
      borderRadius: 10,
      fillColor: 'white',
      filterButNotDisplay: true,
      width: 16,
      style: { // text style
        color: '#FF0000'
      },
      states: {
        hover: {
          fillColor: 'white',
          color: '#FF0000',
          lineColor: '#FF0000'
        }
      },
      onSeries: 'low_level_limit',
      title: 'AB'

    },
    {
      key: 'statistical',
      value: 'statistical',
      type: CHART_SERIES_TYPE.ALARMS,
      translationSlug: 'website.equipment.detail.charts.alarms_fluids',
      valueSuffix: '',

      checked: true,
      linkTo: ['events.fluid_alarm'],
      showInChart: true,
      tooltipSeries: null,
      dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
      color: '#ab0303',
      shape: 'squarepin',
      borderRadius: 30,
      fillColor: '#ab0303',
      width: 16,
      style: { // text style
        color: 'white'
      },
      states: {
        hover: {
          fillColor: '#ab0303',
          color: '#ab0303',
          lineColor: '#ab0303'
        }
      },
      onSeries: 'average_reference',
      title: 'AF'
    },
    {
      key: 'technical',
      value: 'technical',
      type: CHART_SERIES_TYPE.ALARMS,
      translationSlug: 'website.equipment.detail.charts.alarms_technicals',
      valueSuffix: '',
      checked: false,
      showInChart: true,
      dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
      tooltipSeries: null,
      color: '#ffa502',
      shape: 'circlepin',
      borderRadius: 10,
      fillColor: '#ffa502',
      width: 16,
      style: { // text style
        color: 'white'
      },
      states: {
        hover: {
          fillColor: '#ffa502',
          color: '#ffa502',
          lineColor: '#ffa502'
        }
      },
      onSeries: null,
      title: 'T'
    },
    {
      key: 'refills',
      type: CHART_SERIES_TYPE.REFILLS,
      translationSlug: 'website.equipment.detail.charts.refills',
      valueSuffix: '',
      checked: false,
      showInChart: true,
      dataType: [CHART_DATA_TYPE.INST, CHART_DATA_TYPE.AVG],
      tooltipSeries: null,
      color: '#b2df8a',
      shape: 'circlepin',
      borderRadius: null,
      onSeries: null,
      title: 'R'
    }
  ]
};
@Injectable({
  providedIn: 'root'
})
export class CommonSeriesChartService {
  public trInstant: (key: string | Array<string>, interpolateParams?: Object) => string | any;
  constructor(private unitConversionService: UnitConversionService, private userPreferencesService: UserPreferencesService, private dateService: DateService , private translateService: TranslateService, private seriesGrouperService: SeriesGrouperService) {
    this.trInstant = this.translateService.instant;
    this.trInstant = this.trInstant.bind(this.translateService);
  }
  public buildSeriesMovingAverage(filterItem: any, chartData: PagedResult<InstChartData | AvgChartData>, activeTab: CHART_DATA_TYPE ): object {
    let period = 24;
    if (activeTab === CHART_DATA_TYPE.INST){
      period = 100;
    }
    const data = this.calculateMovingAverage(chartData.itemsPage.map((item) => {
     let value = item[filterItem.value];
     if (filterItem.valueSuffix){
        value = this.userPreferencesService.convertUnitByPreference(value, filterItem.valueSuffix);
      }
     return [this.dateService.moment(item.date_time).valueOf(), value];
    }), period);

    const seriesObject = {
      id: `${filterItem.key}`,
      color: 'orange',
      borderColor: filterItem.showInChart && filterItem.dataType.includes(activeTab) ? COLORS[filterItem.colorIndex] : 'transparent',
      showInLegend: filterItem.showInChart && filterItem.dataType.includes(activeTab) ? undefined : false,
      name: this.trInstant(filterItem.translationSlug),
      data,
      showInNavigator: true,
      tooltip: {
        valueSuffix: filterItem.valueSuffix,
        valueSuffixConverted: this.userPreferencesService.getUnitNameByPreference(filterItem.valueSuffix),
        timeUnit : filterItem.timeUnit
      },
      marker: {
        enabled: false
      },
      boostThreshold: 1, // Set the boost threshold for triggering Boost
      // Optional: Enable GPU translations for better performance
      boost: {
        useGPUTranslations: true
      },
      yAxis: filterItem.yAxis ?? 0
    };

    return seriesObject;
  }

  private  calculateMovingAverage(data: [number, number][], period: number): [number, number][] {
    const movingAverage: [number, number][] = [];
    let sum = 0;

    for (let i = 0; i < data.length; i++) {
      const value = data[i][1];
      const date = data[i][0];
      sum += value;
      if (i >= period){
        sum = sum - data[i - period][1] ;
      }

      movingAverage.push([date, parseFloat((sum / Math.min(i + 1, period)).toFixed(2))]);
    }

    return movingAverage;
  }
  public buildSeriesObject(filterItem: any, chartData: PagedResult<InstChartData | AvgChartData>, activeTab: CHART_DATA_TYPE): object {
    const seriesObject = {
      id: filterItem.key,
      color: filterItem.showInChart && filterItem.dataType.includes(activeTab) ? COLORS[filterItem.colorIndex] : 'transparent',
      borderColor: filterItem.showInChart && filterItem.dataType.includes(activeTab) ? COLORS[filterItem.colorIndex] : 'transparent',
      showInLegend: filterItem.showInChart && filterItem.dataType.includes(activeTab) ? undefined : false,
      name: this.trInstant(filterItem.translationSlug),
      data: chartData.itemsPage.map(item => {
        let value = item[filterItem.key];
        if (filterItem.valueSuffix && this.unitConversionService[filterItem.valueSuffix]){
          value = this.userPreferencesService.convertUnitByPreference(value, filterItem.valueSuffix);
        }
        return [this.dateService.moment(item.date_time).valueOf(), value];
      }),
      showInNavigator: true,
      tooltip: {
        valueSuffix: filterItem.valueSuffix,
        valueSuffixConverted: this.userPreferencesService.getUnitNameByPreference(filterItem.valueSuffix)
      },
      marker: {
        enabled: false  // Hides the points
      },
      boostThreshold: 1,
      boost: {
        useGPUTranslations: true
      },
      yAxis: filterItem.yAxis ?? 0
    };

    return seriesObject;
  }

  public  buildRefillsSeriesObject(filterItem: any, refillsData: PagedResult<any>, minChartValue: number): object {
    const refillsDataSeries =  refillsData.itemsPage.map(item => [this.dateService.moment(item.date_time).valueOf(), minChartValue]);
    return {
      type: 'flags',
      id: filterItem.key,
      name: this.trInstant(filterItem.translationSlug),
      color: filterItem.color,
      shape: filterItem.shape,
      borderRadius: filterItem.borderRadius,
      navigatorOptions: {
        type: 'flags',
        onSeries: undefined,
        data: refillsDataSeries
      },      boostThreshold: 1,
      boost: {
        useGPUTranslations: true
      },
      showInNavigator: true,
      onSeries: filterItem.onSeries,
      data: refillsDataSeries,
      tooltip: {
        pointFormat: '{point.text}'
      }
    };
  }

  public buildAlarmsSeriesObject(filterItem: any, alarmsData: PagedResult<any>): object {
    const alarms = alarmsData.itemsPage.filter(alarm =>
      alarm.alarm_type?.id === filterItem.value
    );

    const tab = alarms.map(alarm => ({
      x: this.dateService.moment(alarm.date_time).valueOf(),
      title: filterItem.title,
      groupedDescription : `${this.trInstant('website.equipment.detail.activity.description_label')} : ${this.trInstant('website.alarms.' + alarm.code_name)}`,
      text: `${this.trInstant('website.equipment.detail.activity.description_label')} : ${this.trInstant('website.alarms.' + alarm.code_name)}<br>${this.trInstant('website.equipment.detail.activity.start_timestamp_label')} : ${this.dateService.moment(alarm.date_time).format(customDateFormat.medium)}</br>${this.trInstant('website.equipment.detail.activity.acquittedOn')} : ${alarm.acquittedOn
        ? this.dateService.moment(alarm.acquittedOn).format(customDateFormat.medium)
        : this.trInstant('website.equipment.detail.charts.ongoing')
      }
  `
    }));


    return {
      type: 'flags',
      id: filterItem.value,
      name: this.trInstant(filterItem.translationSlug),
      navigatorOptions: {
        type: 'flags',
        onSeries: undefined,
        data: tab
      },
      color: filterItem.color,
      shape: filterItem.shape,
      borderRadius: filterItem.borderRadius,
      fillColor: filterItem.fillColor,
      width: filterItem.fillColor,
      style: { // text style
        color: filterItem.style.color
      },
      boostThreshold: 1,
      boost: {
        useGPUTranslations: true
      },
      states: filterItem.states,
      showInNavigator: true,
      onSeries: filterItem.onSeries,
      data: tab,
      tooltip: {
        pointFormat: '{point.text}'
      }
    };
  }

  public buildChartSeries(
    chartData: PagedResult<InstChartData | AvgChartData>,
    alarmsData: PagedResult<any>,
    refillsData: PagedResult<any>,
    filters: any[],
    activeTab: CHART_DATA_TYPE,
    minChartValue: number,
  ): any[] {
    const chartSeries = [];
    for (const filterCategory of filters) {
      for (const filterItem of filterCategory?.filters) {
        if (!filterItem.checked || filterItem.disable) {
          continue;
        }

        if (filterItem.dataType.includes(activeTab)) {
          let series;
          switch (filterItem.type) {
            case CHART_SERIES_TYPE.Y:
              series = this.buildSeriesObject(filterItem, chartData, activeTab);
              break;
            case CHART_SERIES_TYPE.ALARMS:
              series = this.buildAlarmsSeriesObject(filterItem, alarmsData);
              break;
            case CHART_SERIES_TYPE.MOVING_AVERAGE:
              series = this.buildSeriesMovingAverage(filterItem, chartData, activeTab);
              break;
            case CHART_SERIES_TYPE.REFILLS:
              series = this.buildRefillsSeriesObject(filterItem, refillsData, minChartValue);
              break;
          }

          if (series) {
            chartSeries.push(series);
          }

          // Add tooltip series if present
          if (filterItem.tooltipSeries?.length) {
            for (const tooltipSeries of filterItem.tooltipSeries) {
              if (tooltipSeries.dataType.includes(activeTab)) {
                const tooltipSeriesObject = this.buildSeriesObject(tooltipSeries, chartData, activeTab);
                chartSeries.push(tooltipSeriesObject);
              }
            }
          }
        }
      }
    }


    return chartSeries;
  }

  updateSeries(chart: Highcharts.Chart, updatedSeries: any[]): void {
    if (!chart) {
      throw new Error('Chart instance is required to update series.');
    }

    // Create a map of current series by their IDs
    const currentSeriesMap = chart.series.reduce((map: any, serie: any) => {
      if (serie.options.id) {
        map[serie.options.id] = serie;
      }
      return map;
    }, {});

    // Update or add the series
    updatedSeries?.forEach((newSerie: any) => {
      const existingSerie = currentSeriesMap[newSerie.id];

      if (existingSerie) {
        // Update the existing series
        existingSerie.update(newSerie);
      }
    });

    // Redraw and reflow the chart
    chart.redraw();
    chart.reflow();
  }



  public tooltipFormatted(event: any , ctx: any) {
    if (event.points) {
      return event.points.reduce((prev: any, curr: any) => {
        const value = curr.point.options && curr.point.options.title
          ? curr.point.options.title
          : curr.y;

        return `${prev}<br/><span style="color:${curr.color}">\u25CF</span> ${curr.series.name}: ${value} ${curr.series.options.tooltip.valueSuffixConverted || ''} ${curr.series.options.tooltip.timeUnit ? '/' + curr.series.options.tooltip.timeUnit : '' }`;
      }, `<i>${this.trInstant('website.equipment.detail.charts.drag_to_zoom')}</i><br/><b>${this.dateService.moment(event.x).format(customDateFormat.medium)}</b>`);
    } else if (event.point) {
      const value = event.point.options && event.point.options.text
        ? event.point.options.text
        : event.point.y;

      return `<b>${this.dateService.moment(event.x).format(customDateFormat.medium)}</b><br/><span style="color:${event.point.color}">\u25CF</span> ${event.series.name}<br>${value}`;
    } else {
      return `<b>${this.dateService.moment(event.x).format(customDateFormat.medium)}</b>`;
    }
  }

  public refreshSeriesByDateRange(event: AxisSetExtremesEventObject, ctx: any) {
    // TODO to improve not good convention to use ctx in service
    const minDate = new Date(ctx.dateService.moment(event.min).valueOf());
    const maxDate = new Date(ctx.dateService.moment(event.max).valueOf());
    const updatedSeries = ctx.groupSeries(minDate, maxDate);
    const chart = ctx.Highcharts.charts.filter(Boolean)[0];

    this.updateSeries(chart, updatedSeries);
  }

  public applyFilterToFilterCategory(filterCategory: FilterCategory[], activeTab): any {
    return JSON.parse(JSON.stringify(filterCategory)).map((filterCategoryItem) => {
      filterCategoryItem.filters = filterCategoryItem.filters.filter((filter) => {
        return filter.dataType.includes(activeTab) && !filter.hide;
      }).map((filter) => {
        if (filter?.valueSuffix) {
          filter.valueSuffixConverted = this.userPreferencesService.getUnitNameByPreference(filter?.valueSuffix);
        }
        filter.tooltip = filter?.tooltip?.map((tooltip) => {
          tooltip.valueSuffixConverted = this.userPreferencesService.getUnitNameByPreference(tooltip?.valueSuffix);
        });
        return filter;
      });
      return filterCategoryItem;
    }).filter((filterCategoryElem: FilterCategory) => filterCategoryElem.filters?.length !== 0 &&  !filterCategoryElem?.hide );
  }

}
